import omit from 'lodash/omit';
import throttle from 'lodash/throttle';
import { temporal } from 'zundo';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { createWithEqualityFn, useStoreWithEqualityFn } from 'zustand/traditional';
import isDeepEqual from 'fast-deep-equal';
import { type Element } from '../types/element';
// 拷贝自web-pro
import { createStoreUtils } from '../utils/createStoreUtils';

interface ProjectState {
  __version: number; // 版本号
  __initialized: boolean; // 是否初始化
  __skipTracking: boolean; // 是否跳过跟踪
  __skipSaving: boolean; // 是否跳过保存

  elements: Element[]; // 画布元素
  selection: []; // 画布选区
  editingElementId: null; // 正在编辑的元素ID
  currentElementTool: null; // 当前选中的元素工具
  currentNodeId: null; // 当前选中的流节点

  // 画布尺寸
  canvasSize: [number, number];

  // 画布视口
  viewport: {
    x: number;
    y: number;
    scale: number;
  };

  // 任务数据
  tasks: {
    current: null;
    fe_queue: Record<string, any>[];
    map: Record<string, any>;
  };

  units: Record<string, any>; // 项目的独立工作单元
  nodeMap: Record<string, any>; // 节点map

  // ===流模式相关数据===
  validProjectId: boolean; // 有效的项目Id
  projectTitle: string; // 项目标题
  workSpaceUuid: string; // 工作空间UUID
  flows: Record<string, any>[];
  flowScale: number; // 流轨道缩放
  isFoldFlow: boolean; // 流模式展开状态, 默认收起
  autoRunningFlowId: string; // 正在自动运行的流id
  autoRunningFlowNodeId: string; // 自动运行的流节点ID
}

const initialProjectState: ProjectState = {
  __version: 1,
  __initialized: false,
  __skipTracking: false,
  __skipSaving: false,

  canvasSize: [0, 0],

  viewport: {
    scale: 0.25,
    x: 0,
    y: 0,
  },

  validProjectId: true,

  projectTitle: '独立工作单元',
  workSpaceUuid: '',
  // 任务数据
  tasks: {
    current: null,
    fe_queue: [],
    map: {},
  },

  // 画布元素
  elements: [],

  // 画布选区
  selection: [],

  // 正在编辑的元素ID
  editingElementId: null,
  // 当前选中的元素工具
  currentElementTool: null,

  // 当前选中的流节点
  currentNodeId: null,

  // 项目的独立工作单元
  units: {},

  nodeMap: {},

  // ===流模式相关数据===
  flows: [],
  flowScale: 1, // 流轨道缩放
  isFoldFlow: true, // 流模式展开状态, 默认收起
  autoRunningFlowId: '', // 正在自动运行的流id
  autoRunningFlowNodeId: '', // 自动运行的流节点ID
};

/**
 * 画布核心数据，这一部分是支持撤销重做，然后需要存储到云端的
 */
export const _useProjectStore = createWithEqualityFn(
  temporal(
    subscribeWithSelector(() => ({
      ...initialProjectState,
    })),
    {
      limit: 100,
      diff: (_, currentState) => {
        // 如果传递了 __skipTracking 标记，那么就不需要记录历史了
        if (currentState.__skipTracking) return null;

        return undefined;
      },
      partialize: (state) => omit(state, ['tasks', 'viewport']),
      // equality: isDeepEqual,
      handleSet: (set) =>
        throttle((state) => {
          set(Object.assign({}, state, { __version: (state.__version || 1) + 1 }));
        }, 100),
    },
  ),
);

export const { useStore: useProjectState, getState: getProjectState } = createStoreUtils(_useProjectStore);

export const useElement = (elementId: string) => {
  return useProjectState((state) => state.elements.find((element) => element.id === elementId));
};

export const useUnit = (nodeId: string) => {
  return useProjectState((state) => state.nodeMap[nodeId]?.data || {});
};

export const useUnitData = (nodeId, fieldName) => {
  return useProjectState((state) => {
    const data = state.nodeMap[nodeId || state.currentNodeId]?.data?.data || {};

    if (fieldName) {
      return data[fieldName];
    } else {
      return data;
    }
  });
};

export const useTraceableProjectState = (selector, equality) =>
  useStoreWithEqualityFn(_useProjectStore.temporal, selector, equality);

export const setProjectState = (
  nextState: Partial<ProjectState>,
  { __replace = false, __skipTracking = false }: { __replace?: boolean; __skipTracking?: boolean } = {},
) => {
  _useProjectStore.setState((prevState) => {
    return {
      ...nextState,
      __skipSaving: Boolean(nextState.__skipSaving),
      __skipTracking,
    };
  }, __replace);
};

globalThis.STARFLOW = { getProjectState, setProjectState };
